import {
  ChatAvatar,
  ChatItem,
  ChatItemMeta,
  ChatMetaHeader,
  ContextMenuTrigger,
  SessionCollapseAvatarIcon,
  baseSizeMap,
  ItemWithExpireAnimate,
} from '@halo/ui-chat-react';
import type { BoxProps } from '@chakra-ui/react';
import { Box, Center } from '@chakra-ui/react';
import type { Session } from '@/store/client';
import { useActionsStore } from '@/store/actions';
import {
  AccountType,
  isSessionCategory,
  SessionCategoryPrefix,
  useStaticAppStore,
} from '@/store/client';
import { useRobotInfo } from '@/components/RobotProfileDialog/Helper';
import { useI18nStore } from '@/store/i18n/store';
import { ChatMetaContentWithData } from './session-item-with-data';

export interface SessionItemProps extends BoxProps {
  session: Session;
  isCollapseChat?: boolean;
  active?: boolean;
  ref?: any | undefined;
}

export const SessionItem = memo(function SessionItem({
  session,
  isCollapseChat = false,
  active,
  ...props
}: SessionItemProps) {
  const { client } = useStaticAppStore();
  const { chat_id, name, last_message, update_at, user_info } = session;
  const formatTimeSimple = useI18nStore((store) => store.formatTimeSimple);

  const fetchProfileUser = useActionsStore((store) => store.fetchProfileUser);

  const isGroup = useMemo(
    () => isSessionCategory(chat_id, SessionCategoryPrefix.Group),
    [chat_id]
  );

  const handleAvatarClick = useCallback(async () => {
    if (!isGroup && user_info?.user_id) {
      fetchProfileUser(user_info.user_id);
    }
  }, [fetchProfileUser, isGroup, user_info?.user_id]);

  const data = useMemo(() => ({ session }), [session]);

  const { fontSize, boxSize } = useMemo(() => {
    const base = baseSizeMap['sm'] ?? 32;
    return {
      fontSize: `calc(${base}px + var(--text-scale-factor, 0px))`,
      boxSize: `calc(${base}px + var(--text-scale-factor, 0px))`,
    };
  }, []);

  const isRobot = useMemo(() => {
    return !isGroup && session?.user_info?.account_type === AccountType.Robot;
  }, [session?.user_info?.account_type, isGroup]);

  const robotInfo = useRobotInfo(session?.user_info);

  const updateAtDisplay = useMemo(() => {
    return formatTimeSimple(update_at);
  }, [formatTimeSimple, update_at]);

  const [doExpireAnimation, setDoExpireAnimation] = useState(false);

  useEffect(() => {
    if (client) {
      const hideSessionsHandler = client.addEventListener('HideSessions', ({ detail }) => {
        const { chat_ids } = detail!;
        if (chat_ids.includes(session.chat_id)) {
          setDoExpireAnimation(true);
        }
      });
      return () => hideSessionsHandler();
    }
  }, [client, session.chat_id]);

  return (
    <Box {...props}>
      <ContextMenuTrigger data={data}>
        <ItemWithExpireAnimate animateDirection="left" doExpireAnimation={doExpireAnimation}>
          <ChatItem session={session} active={active} height="var(--chat-item-content-height)">
            {isCollapseChat ? (
              <Center fontSize={fontSize} boxSize={boxSize}>
                <SessionCollapseAvatarIcon />
              </Center>
            ) : (
              <ChatAvatar
                src={session.avatar}
                duration={session.expire_duration}
                onClick={handleAvatarClick}
                activeShake={!isGroup}
                pointerEvents="none"
                showExpireIcon={!isRobot}
                size="xl"
                isOnACall={session?.isOnACall}
              />
            )}
            <ChatItemMeta>
              <ChatMetaHeader
                sessionName={name}
                state={user_info?.state}
                name={user_info?.name}
                isGroup={isGroup}
                isCollapseChat={isCollapseChat}
                updateAtDisplay={updateAtDisplay}
                operateAccess={session.operateAccess}
                isRobot={isRobot}
                tooltip={robotInfo.tooltip}
                official={robotInfo.isofficial}
              />
              <ChatMetaContentWithData
                last_message={last_message}
                isGroup={isGroup}
                session={session}
                isCollapseChat={isCollapseChat}
                isRobot={isRobot}
              />
            </ChatItemMeta>
          </ChatItem>
        </ItemWithExpireAnimate>
      </ContextMenuTrigger>
    </Box>
  );
});
